import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { logout } from "@/services/slices/auth.slice";
import { changeLanguage } from "@/services/slices/setting.slice";
import Icon from "@/components/Icon";

const NAV_ITEMS = [
  { to: "/console/dashboard", label: "Dashboard", icon: "view-grid-outline" },
  { to: "/console/user-management", label: "User Management", icon: "account-group" },
  { to: "/console/societies", label: "Societies", icon: "account-group-outline" },
  { to: "/console/sponsors", label: "Sponsors", icon: "hand-coin-outline" },
];

/**
 * Admin console shell: sidebar navigation + content outlet.
 * Guards every `/console/*` route: only a logged-in admin may enter.
 */
const ConsoleLayout: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { loggedIn, user_auth, admin } = useSelector((state: any) => state.auth);
  const language = useSelector((state: any) => state.setting.language);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSidebarOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [sidebarOpen]);

  if (!loggedIn || user_auth !== "admin") {
    return <Navigate to="/console/login" replace />;
  }

  const handleLogout = () => {
    setSidebarOpen(false);
    dispatch(logout());
    navigate("/console/login");
  };

  const toggleLanguage = () => {
    dispatch(
      changeLanguage({ language: language === "zh_hant" ? "en" : "zh_hant" }),
    );
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          aria-hidden="true"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        id="console-sidebar"
        className={`fixed inset-y-0 left-0 z-50 flex w-60 shrink-0 flex-col bg-[#1f2937] text-white transition-transform duration-200 lg:static lg:z-auto lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-start justify-between border-b border-white/10 px-5 py-4">
          <div>
            <div className="text-xl font-bold text-[#ffa217]">Uninfo</div>
            <div className="text-xs text-gray-400">{t("Admin Console")}</div>
          </div>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            aria-label={t("Close")}
            title={t("Close")}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-300 transition hover:bg-white/10 hover:text-white lg:hidden"
          >
            <Icon
              iconType="MaterialCommunityIcons"
              name="close"
              size={20}
              color="currentColor"
            />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-3 text-sm transition ${
                  isActive
                    ? "bg-[#ffa217] font-semibold text-white"
                    : "text-gray-300 hover:bg-white/10"
                }`
              }
            >
              <Icon
                iconType="MaterialCommunityIcons"
                name={item.icon}
                size={18}
                color="currentColor"
              />
              {t(item.label)}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 px-5 py-4">
          <div className="truncate text-sm text-gray-300">
            {admin?.username || "Admin"}
          </div>
          <button
            type="button"
            onClick={toggleLanguage}
            className="mt-2 block w-full rounded-lg px-2 py-1 text-left text-xs font-semibold text-[#ffa217] transition hover:bg-white/10"
          >
            {language === "zh_hant" ? "EN" : "繁體"}
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-2 text-xs text-gray-400 transition hover:text-white"
          >
            {t("Logout")}
          </button>
        </div>
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4 lg:hidden">
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            aria-label={t("Menu")}
            title={t("Menu")}
            aria-controls="console-sidebar"
            aria-expanded={sidebarOpen}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-700 transition hover:bg-gray-100 hover:text-[#ffa217]"
          >
            <Icon
              iconType="MaterialCommunityIcons"
              name="menu"
              size={24}
              color="currentColor"
            />
          </button>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-bold text-[#333]">
              Uninfo <span className="text-[#ffa217]">Admin</span>
            </div>
            <div className="truncate text-[11px] text-gray-500">
              {t("Admin Console")}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto bg-[#f5f5f5]">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ConsoleLayout;